import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  IndianRupee,
  Building2,
  Users,
  GitBranch,
  ClipboardList,
  CreditCard,
  Download,
  RefreshCw,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';

const PIE_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#06b6d4', '#ec4899', '#84cc16', '#6366f1'];

const REFRESH_INTERVAL_MS = 60_000;

interface DashboardSummary {
  tenants: {
    total: number;
    active: number;
    trial: number;
    suspended: number;
    pending_approval: number;
    by_business_type: Record<string, number>;
    new_last_7_days: number;
  };
  users: { total: number };
  branches: { total: number; active: number };
  pending_approvals_count: number;
  subscriptions: { active: number; past_due: number };
  commission_summary: Record<string, any>;
  generated_at: string;
}

function StatCard({
  label,
  value,
  icon: Icon,
  gradient,
  sub,
}: {
  label: string;
  value: React.ReactNode;
  icon: any;
  gradient: string;
  sub?: string;
}) {
  return (
    <Card className={`bg-gradient-to-br ${gradient} text-white border-none`}>
      <CardContent className="p-6">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-white/80 font-medium">{label}</p>
            <h3 className="text-3xl font-bold mt-1">{value}</h3>
          </div>
          <div className="p-3 bg-white/15 rounded-lg">
            <Icon size={24} />
          </div>
        </div>
        {sub && <div className="mt-4 text-sm font-medium text-white/80">{sub}</div>}
      </CardContent>
    </Card>
  );
}

export function DashboardView() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchSummary = async (isBackground = false) => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setError('Supabase client not configured.');
      setLoading(false);
      return;
    }
    if (isBackground) setRefreshing(true);
    else setLoading(true);
    setError(null);

    const { data, error: rpcError } = await supabase.rpc('get_admin_dashboard_summary');

    if (rpcError) {
      setError(rpcError.message || 'Not authorized to view this dashboard.');
      if (!isBackground) toast.error(rpcError.message || 'Failed to load dashboard');
    } else {
      setSummary(data as DashboardSummary);
    }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    fetchSummary(false);
    intervalRef.current = setInterval(() => fetchSummary(true), REFRESH_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleExport = () => {
    if (!summary) return;
    const blob = new Blob([JSON.stringify(summary, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard-summary-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="p-6 text-center text-slate-500 dark:text-slate-400">Loading dashboard...</div>;
  }

  if (error) {
    return (
      <Card className="dark:bg-slate-900 dark:border-slate-800 max-w-xl mx-auto mt-10">
        <CardContent className="p-8 text-center space-y-3">
          <h3 className="text-lg font-bold text-red-600 dark:text-red-400">Could not load dashboard</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">{error}</p>
          <Button onClick={() => fetchSummary(false)} className="bg-primary hover:bg-primary/90 text-white">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!summary) return null;

  const businessTypeData = Object.entries(summary.tenants.by_business_type || {}).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value: value as number,
  }));

  const commissionEntries = Object.entries(summary.commission_summary || {});

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold dark:text-white">System Overview</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Live health and wealth data of BahiBox Ecosystem.
            {refreshing && <span className="ml-2 text-xs text-blue-500 inline-flex items-center gap-1"><RefreshCw size={12} className="animate-spin" /> refreshing…</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2 dark:border-slate-700 dark:text-white" onClick={() => fetchSummary(false)}>
            <RefreshCw size={16} /> Refresh
          </Button>
          <Button onClick={handleExport} className="bg-primary hover:bg-primary/90 text-white gap-2">
            <Download size={16} /> Export Report
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          label="Total Tenants"
          value={summary.tenants.total}
          icon={Building2}
          gradient="from-blue-500 to-indigo-600"
          sub={`${summary.tenants.active} active • ${summary.tenants.new_last_7_days} new (7d)`}
        />
        <StatCard
          label="Total Users"
          value={summary.users.total}
          icon={Users}
          gradient="from-purple-500 to-pink-600"
          sub="All accounts across tenants"
        />
        <StatCard
          label="Branches"
          value={summary.branches.total}
          icon={GitBranch}
          gradient="from-emerald-500 to-teal-600"
          sub={`${summary.branches.active} active`}
        />
        <StatCard
          label="Pending Approvals"
          value={summary.pending_approvals_count}
          icon={ClipboardList}
          gradient="from-orange-500 to-red-600"
          sub={summary.pending_approvals_count > 0 ? 'Needs attention' : 'All caught up'}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatCard
          label="Subscriptions Active"
          value={summary.subscriptions.active}
          icon={CreditCard}
          gradient="from-cyan-500 to-blue-600"
          sub={`${summary.subscriptions.past_due} past due`}
        />
        <StatCard
          label="Tenant Status"
          value={`${summary.tenants.trial} trial / ${summary.tenants.suspended} suspended`}
          icon={IndianRupee}
          gradient="from-slate-600 to-slate-800"
          sub={`${summary.tenants.pending_approval} pending approval`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="dark:bg-slate-900 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="dark:text-white">Tenants by Business Type</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            {businessTypeData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">No tenant data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={businessTypeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                    {businessTypeData.map((_, idx) => (
                      <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="dark:bg-slate-900 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="dark:text-white">Commission Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {commissionEntries.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm py-16">No commission data yet</div>
            ) : (
              <div className="space-y-3">
                {commissionEntries.map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between py-2 border-b dark:border-slate-800 last:border-0">
                    <span className="text-sm text-slate-500 dark:text-slate-400 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="font-semibold dark:text-white">
                      {typeof value === 'number'
                        ? (key.toLowerCase().includes('amount') || key.toLowerCase().includes('revenue') || key.toLowerCase().includes('commission')
                            ? `₹ ${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                            : value.toLocaleString('en-IN'))
                        : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <p className="text-xs text-slate-400 dark:text-slate-600 text-right">
        Last generated: {summary.generated_at ? new Date(summary.generated_at).toLocaleString() : 'just now'}
      </p>
    </div>
  );
}

export default DashboardView;
